/**
 * Composable for Chart.js impact visualization
 * Handles chart creation, update, and destruction
 */

import type { Ref } from 'vue'
import type { ChartConfiguration } from 'chart.js'
import type { ImpactChartData } from '@/types/skillVisualization'
import { useChartLifecycle } from '@/composables/useChartLifecycle'

/**
 * Create Chart.js configuration for skill impact visualization
 * Creates three datasets per impact: MIN (dashed), MAX (dashed + fill), AVG (solid)
 */
export function createChartConfig(
  impactDatasets: ImpactChartData[],
  xAxisLabel: string = 'Stat Value'
): ChartConfiguration {
  const datasets: any[] = []

  // For each impact, create three datasets (min, max, avg)
  impactDatasets.forEach((impactData, index) => {
    const { label, color, data } = impactData
    const rgb = `${color.r}, ${color.g}, ${color.b}`

    // 1. MIN dataset (dashed line, semi-transparent)
    datasets.push({
      label: `${label} (Min)`,
      data: data.minValues,
      borderColor: `rgba(${rgb}, 0.4)`,
      backgroundColor: 'transparent',
      borderWidth: 1.5,
      borderDash: [4, 4],
      pointRadius: 0,
      pointHoverRadius: 4,
      order: 3, // Render last (behind)
      hidden: false,
      yAxisID: 'y',
      impactIndex: index,
      datasetType: 'min'
    })

    // 2. MAX dataset (dashed line with fill to previous, creating corridor)
    datasets.push({
      label: `${label} (Max)`,
      data: data.maxValues,
      borderColor: `rgba(${rgb}, 0.4)`,
      backgroundColor: `rgba(${rgb}, 0.15)`, // Shaded corridor
      borderWidth: 1.5,
      borderDash: [4, 4],
      pointRadius: 0,
      pointHoverRadius: 4,
      fill: '-1', // Fill down to previous dataset (MIN)
      order: 2,
      hidden: false,
      yAxisID: 'y',
      impactIndex: index,
      datasetType: 'max'
    })

    // 3. AVG dataset (solid line, main reference)
    datasets.push({
      label: label,
      data: data.avgValues,
      borderColor: `rgba(${rgb}, 1)`,
      backgroundColor: 'transparent',
      borderWidth: 2.5,
      pointRadius: 2,
      pointHoverRadius: 5,
      pointBackgroundColor: `rgba(${rgb}, 1)`,
      pointBorderColor: '#fff',
      pointBorderWidth: 1,
      order: 1, // Render on top
      hidden: false,
      yAxisID: 'y',
      impactIndex: index,
      datasetType: 'avg'
    })
  })

  return {
    type: 'line',
    data: {
      labels: impactDatasets[0]?.data.statValues || [],
      datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            color: '#f3e7cf',
            font: {
              size: 12,
              family: "'Roboto', sans-serif"
            },
            padding: 10,
            usePointStyle: true,
            filter: (legendItem: any) => {
              // Only show average lines in legend (hide min/max)
              return !legendItem.text.includes('(Min)') && !legendItem.text.includes('(Max)')
            }
          },
          onClick: (e: any, legendItem: any, legend: any) => {
            const index = legendItem.datasetIndex
            const chart = legend.chart

            // Toggle all three datasets for this impact (min, avg, max)
            const impactIndex = Math.floor(index / 3)
            const minIndex = impactIndex * 3
            const maxIndex = impactIndex * 3 + 1
            const avgIndex = impactIndex * 3 + 2

            const minMeta = chart.getDatasetMeta(minIndex)
            const maxMeta = chart.getDatasetMeta(maxIndex)
            const avgMeta = chart.getDatasetMeta(avgIndex)

            const newHiddenState = !avgMeta.hidden

            minMeta.hidden = newHiddenState
            maxMeta.hidden = newHiddenState
            avgMeta.hidden = newHiddenState

            chart.update()
          }
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(12, 14, 15, 0.95)',
          titleColor: '#fada95',
          bodyColor: '#f3e7cf',
          borderColor: 'rgba(250, 218, 149, 0.4)',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: (context: any) => {
              const label = context.dataset.label || ''
              const value = context.parsed.y.toFixed(1)
              return `${label}: ${value}`
            }
          }
        },
        title: {
          display: false
        }
      },
      scales: {
        x: {
          type: 'linear',
          title: {
            display: true,
            text: xAxisLabel,
            color: '#fada95',
            font: {
              size: 13,
              weight: '500'
            }
          },
          grid: {
            color: 'rgba(250, 218, 149, 0.08)',
            drawBorder: false
          },
          ticks: {
            color: '#9a9078',
            font: {
              size: 11
            }
          }
        },
        y: {
          type: 'linear',
          title: {
            display: true,
            text: 'Impact Value',
            color: '#fada95',
            font: {
              size: 13,
              weight: '500'
            }
          },
          grid: {
            color: 'rgba(250, 218, 149, 0.08)',
            drawBorder: false
          },
          ticks: {
            color: '#9a9078',
            font: {
              size: 11
            }
          },
          beginAtZero: true
        }
      }
    }
  }
}

export function useImpactChart(canvasRef: Ref<HTMLCanvasElement | null>) {
  const { chartInstance, render, destroy } = useChartLifecycle(canvasRef)

  /**
   * Initialize or update the chart
   */
  const updateChart = async (impactDatasets: ImpactChartData[], xAxisLabel: string = 'Stat Value') => {
    if (!canvasRef.value) {
      console.error('Canvas ref is null')
      return
    }

    const config = createChartConfig(impactDatasets, xAxisLabel)
    await render(config as ChartConfiguration)
  }

  /**
   * Destroy chart instance
   */
  const destroyChart = () => {
    destroy()
  }

  /**
   * Update chart data without recreating
   */
  const updateChartData = (impactDatasets: ImpactChartData[]) => {
    if (!chartInstance.value) return

    const chart = chartInstance.value
    const config = createChartConfig(impactDatasets)

    // Update data
    chart.data.labels = config.data.labels
    chart.data.datasets = config.data.datasets as any

    chart.update('none') // Update without animation
  }

  return {
    chartInstance,
    updateChart,
    destroyChart,
    updateChartData
  }
}

export default useImpactChart
